import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../config/api';

export interface Venue {
  id: string;
  name: string;
  city?: string;
  state?: string;
  venue_group_id?: string;
}

type SelectableEntry =
  | {
      kind: 'group';
      key: string;
      groupId: string;
      headerLabel: string;
      members: Venue[];
      representative: Venue;
    }
  | { kind: 'venue'; key: string; venue: Venue };

export interface GroupedVenueSearchPickerProps {
  id: string;
  placeholder: string;
  committedLabel: string;
  token: string;
  /** When not `'all'`, passed as `region` on venue list search */
  regionFilter: string;
  debounceMs?: number;
  emptyHint?: string;
  /** Picked catalog venue; `displayLabel` is the row shown after close (group header or venue line) */
  onPick: (venue: Venue, displayLabel: string) => void;
  onClear?: () => void;
  clearAriaLabel?: string;
}

function mergeByVenueId(prev: Venue[], more: Venue[]): Venue[] {
  const m = new Map<string, Venue>();
  for (const v of prev) m.set(v.id, v);
  for (const v of more) m.set(v.id, v);
  return Array.from(m.values());
}

function formatVenueLine(v: Venue) {
  const loc = v.city && v.state ? ` (${v.city}, ${v.state})` : '';
  return `${v.name}${loc} — ${v.id}`;
}

export default function GroupedVenueSearchPicker(props: GroupedVenueSearchPickerProps) {
  const {
    id,
    placeholder,
    committedLabel,
    token,
    regionFilter,
    debounceMs = 400,
    emptyHint = 'Type at least 2 non-space characters to search.',
    onPick,
    onClear,
    clearAriaLabel = 'Clear selection',
  } = props;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [flatList, setFlatList] = useState<Venue[]>([]);
  const [listLastKey, setListLastKey] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [groupLoading, setGroupLoading] = useState(false);
  const [entries, setEntries] = useState<SelectableEntry[]>([]);
  const [highlight, setHighlight] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const skipNextFetch = useRef(false);

  const fetchVenuePage = useCallback(
    async (q: string, lastKey?: string) => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (q.trim().length >= 2) params.set('q', q.trim());
      if (regionFilter !== 'all') params.set('region', regionFilter);
      if (lastKey) params.set('last_key', lastKey);
      return apiRequest<{ venues: Venue[] | null; last_key?: string }>(
        `/admin/venues?${params.toString()}`,
        token
      );
    },
    [token, regionFilter]
  );

  const runFetch = useCallback(
    async (q: string, append: boolean, pageKey?: string) => {
      if (!token) {
        setFlatList([]);
        setListLastKey(undefined);
        setLoading(false);
        return;
      }
      const t = q.trim();
      if (t.length < 2 && !append) {
        setFlatList([]);
        setListLastKey(undefined);
        setLoading(false);
        return;
      }
      if (t.length < 2 && append) return;

      setLoading(true);
      try {
        const r = await fetchVenuePage(q, pageKey);
        const chunk = r.venues || [];
        if (append) {
          setFlatList((prev) => mergeByVenueId(prev, chunk));
        } else {
          setFlatList(chunk);
        }
        setListLastKey(r.last_key || undefined);
        setHighlight(0);
      } catch {
        if (!append) setFlatList([]);
        setListLastKey(undefined);
      } finally {
        setLoading(false);
      }
    },
    [fetchVenuePage, token]
  );

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runFetch(query, false);
    }, debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, debounceMs, runFetch]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (flatList.length === 0) {
      setEntries([]);
      setGroupLoading(false);
      return;
    }

    let cancelled = false;
    setGroupLoading(true);

    void (async () => {
      try {
        const byGroup = new Map<string, Venue[]>();
        const solo: Venue[] = [];

        for (const v of flatList) {
          const gid = (v.venue_group_id || '').trim();
          if (gid) {
            if (!byGroup.has(gid)) byGroup.set(gid, []);
            byGroup.get(gid)!.push(v);
          } else {
            solo.push(v);
          }
        }

        byGroup.forEach((vs) => {
          vs.sort((a, b) => a.name.localeCompare(b.name));
        });
        solo.sort((a, b) => a.name.localeCompare(b.name));

        const groupIds = Array.from(byGroup.keys());

        type GroupResolved = {
          gid: string;
          groupName: string;
          members: Venue[];
        };

        const resolvedGroups = await Promise.all(
          groupIds.map(async (gid): Promise<GroupResolved> => {
            const searchHits = byGroup.get(gid)!;
            const hitById = new Map(searchHits.map((v) => [v.id, v]));
            try {
              const detail = await apiRequest<{
                venueGroup: { id: string; name: string };
                venueList?: Array<{ id: string; name: string; city?: string; state?: string }>;
              }>(`/admin/venues/groups/${encodeURIComponent(gid)}`, token);
              const groupName = detail.venueGroup?.name?.trim() || gid;
              const apiRows = detail.venueList || [];
              const seen = new Set<string>();
              const members: Venue[] = [];

              for (const row of apiRows) {
                if (!row.id || seen.has(row.id)) continue;
                seen.add(row.id);
                const hit = hitById.get(row.id);
                members.push({
                  id: row.id,
                  name: hit?.name || row.name,
                  city: hit?.city ?? row.city,
                  state: hit?.state ?? row.state,
                  venue_group_id: gid,
                });
              }
              // Include search hits not yet on the group payload (e.g. backfill lag)
              for (const v of searchHits) {
                if (seen.has(v.id)) continue;
                seen.add(v.id);
                members.push({ ...v, venue_group_id: gid });
              }
              members.sort((a, b) => a.name.localeCompare(b.name));
              return { gid, groupName, members };
            } catch {
              const members = searchHits
                .map((v) => ({ ...v, venue_group_id: gid }))
                .sort((a, b) => a.name.localeCompare(b.name));
              return { gid, groupName: gid, members };
            }
          })
        );
        if (cancelled) return;

        const groupedMemberIds = new Set<string>();
        for (const g of resolvedGroups) {
          for (const m of g.members) {
            groupedMemberIds.add(m.id);
          }
        }

        const grouped: SelectableEntry[] = resolvedGroups
          .map((g) => ({
            kind: 'group' as const,
            key: `g:${g.gid}`,
            groupId: g.gid,
            headerLabel: `${g.groupName} (${g.gid})`,
            members: g.members,
            representative: g.members[0],
          }))
          .sort((a, b) => a.headerLabel.localeCompare(b.headerLabel));

        const singles: SelectableEntry[] = solo
          .filter((v) => !groupedMemberIds.has(v.id))
          .map((v) => ({
            kind: 'venue' as const,
            key: `v:${v.id}`,
            venue: v,
          }));

        if (!cancelled) {
          setEntries([...grouped, ...singles]);
        }
      } finally {
        if (!cancelled) {
          setGroupLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [flatList, token]);

  useEffect(() => {
    setHighlight(0);
  }, [entries.length]);

  const displayValue = open ? query : committedLabel;

  const pickEntry = (e: SelectableEntry) => {
    if (e.kind === 'group') {
      onPick(e.representative, e.headerLabel);
    } else {
      onPick(e.venue, formatVenueLine(e.venue));
    }
    setOpen(false);
    setQuery('');
    setFlatList([]);
    setEntries([]);
    setListLastKey(undefined);
  };

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (!open && (ev.key === 'ArrowDown' || ev.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (ev.key === 'Escape') {
      ev.preventDefault();
      setOpen(false);
      return;
    }
    if (entries.length === 0) return;
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setHighlight((h) => Math.min(h + 1, entries.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      const e = entries[highlight];
      if (e) {
        skipNextFetch.current = true;
        pickEntry(e);
      }
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          id={id}
          type="text"
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            setQuery('');
          }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          style={{
            flex: 1,
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
          }}
        />
        {onClear && committedLabel && (
          <button type="button" className="btn-sm btn-secondary" onClick={onClear} aria-label={clearAriaLabel}>
            Clear
          </button>
        )}
      </div>
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 50,
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 320,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          {loading && flatList.length === 0 && (
            <div style={{ padding: '0.75rem', color: '#666' }}>Loading…</div>
          )}
          {!loading && flatList.length === 0 && query.trim().length < 2 && (
            <div style={{ padding: '0.75rem', color: '#666' }}>{emptyHint}</div>
          )}
          {!loading && flatList.length === 0 && query.trim().length >= 2 && (
            <div style={{ padding: '0.75rem', color: '#666' }}>No matches.</div>
          )}
          {flatList.length > 0 && groupLoading && (
            <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#666' }}>
              Resolving venue groups…
            </div>
          )}
          {entries.map((e, idx) => {
            if (e.kind === 'venue') {
              const isHi = highlight === idx;
              return (
                <div
                  key={e.key}
                  role="option"
                  aria-selected={isHi}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => {
                    skipNextFetch.current = true;
                    pickEntry(e);
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    background: isHi ? '#e3f2fd' : undefined,
                  }}
                >
                  {formatVenueLine(e.venue)}
                </div>
              );
            }

            const isHi = highlight === idx;
            return (
              <div key={e.key}>
                <div
                  role="option"
                  aria-selected={isHi}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => {
                    skipNextFetch.current = true;
                    pickEntry(e);
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: isHi ? '#e3f2fd' : undefined,
                  }}
                >
                  {e.headerLabel}
                </div>
                {e.members.map((m) => (
                  <div
                    key={m.id}
                    aria-hidden
                    style={{
                      padding: '0.25rem 0.75rem 0.35rem 1.5rem',
                      fontSize: '0.85rem',
                      fontStyle: 'italic',
                      color: '#555',
                      userSelect: 'none',
                    }}
                  >
                    {formatVenueLine(m)}
                  </div>
                ))}
              </div>
            );
          })}
          {listLastKey && flatList.length > 0 && (
            <div style={{ borderTop: '1px solid #eee' }}>
              <button
                type="button"
                className="btn-sm"
                style={{ width: '100%', borderRadius: 0 }}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => void runFetch(query, true, listLastKey)}
              >
                Load more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
